/* global google */
import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, TextField, FormLabel, TextFieldProps, debounce } from '@mui/material';
import Popper from '@mui/material/Popper';
import type { PopperProps } from '@mui/material/Popper';
import _ from 'lodash';

type AutocompletePrediction = google.maps.places.AutocompletePrediction;
type ComponentRestrictions = google.maps.places.ComponentRestrictions;

type onSelectPlaceType = (
  selectAddress: (address: string) => void,
  place: { suggestion: string; placeId: string }
) => void;

type MetaType = {
  error?: string;
  touched?: boolean;
  dirty?: boolean;
};

const getDefaultSelectedValue = (value?: string): AutocompletePrediction | null => {
  if (!value) {
    return null;
  }
  return {
    description: value,
    place_id: '',
  } as AutocompletePrediction;
};

interface PlacesAutocompleteFieldProps {
  inputProps: TextFieldProps & {
    onChange?: (value: string) => void;
    onSelectPlace?: onSelectPlaceType;
    value?: string;
    meta?: MetaType;
    disableUnderline?: boolean;
  };
  afterSelect?: (suggestion: string, placeId: string) => void;
  componentRestrictions?: ComponentRestrictions;
  sx?: React.CSSProperties;
}

const PlacesAutocompleteField = ({
  inputProps,
  afterSelect,
  componentRestrictions,
  sx,
}: PlacesAutocompleteFieldProps) => {
  const autocompleteService = React.useRef<google.maps.places.AutocompleteService | null>(null);

  const HighZPopper = (props: PopperProps) => (
    <Popper {...props} style={{ ...(props.style || {}), zIndex: 5000 }} />
  );

  const [inputValue, setInputValue] = useState(inputProps.value || '');
  const [options, setOptions] = useState<AutocompletePrediction[]>([]);
  const [value, setValue] = useState<AutocompletePrediction | null>(
    getDefaultSelectedValue(inputProps.value)
  );

  useEffect(() => {
    if (!autocompleteService.current && window.google) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
    }
  }, [autocompleteService.current, window.google]);

  const fetch = useMemo(
    () =>
      debounce(
        (
          request: {
            input: string;
            componentRestrictions?: ComponentRestrictions;
          },
          callback: (results: AutocompletePrediction[] | null) => void
        ) => {
          if (autocompleteService.current) {
            autocompleteService.current.getPlacePredictions(request, callback);
          }
        },
        400
      ),
    []
  );

  useEffect(() => {
    if (!autocompleteService.current) {
      return;
    }

    if (inputValue === '') {
      setOptions(value ? [value] : []);
      return;
    }

    fetch({ input: inputValue, componentRestrictions }, (results) => {
      const currentOptions = value ? [value] : [];
      const resultOptions = results ? [...results] : [];
      const newOptions = _.uniqBy([...currentOptions, ...resultOptions], 'description');

      setOptions(newOptions);
    });
  }, [value, inputValue, fetch, componentRestrictions]);

  useEffect(() => {
    if (inputProps.value) {
      setInputValue(inputProps.value);
      setValue(getDefaultSelectedValue(inputProps.value));
    }
  }, [inputProps.value]);

  const handleInputChange = (_event: any, newInputValue: string) => {
    setInputValue(newInputValue);
  };

  const handleChange = (
    _event: React.SyntheticEvent<Element, Event>,
    newValue: google.maps.places.AutocompletePrediction | null
  ) => {
    setValue(newValue);
    setOptions(newValue ? [newValue, ...options] : options);

    if (inputProps.onChange) {
      inputProps.onChange(newValue?.description || '');
    }

    if (newValue && inputProps.onSelectPlace) {
      const selectAddress = (address: string) => {
        if (inputProps.onChange) {
          inputProps.onChange(address);
        }
      };

      inputProps.onSelectPlace(selectAddress, {
        suggestion: newValue.description,
        placeId: newValue.place_id,
      });
    }

    if (newValue && afterSelect) {
      afterSelect(newValue.description, newValue.place_id);
    }
  };

  return (
    <Autocomplete
      options={options}
      value={value}
      onInputChange={handleInputChange}
      filterOptions={(x) => x}
      autoComplete
      includeInputInList
      filterSelectedOptions
      onChange={handleChange}
      disabled={inputProps.disabled}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
      isOptionEqualToValue={(option, currentValue) =>
        option.description === currentValue.description
      }
      renderInput={(params) => (
        <TextField
          {...params}
          {..._.omit(inputProps, ['meta', 'onSelectPlace', 'onChange', 'onBlur'])}
          variant="standard"
          error={!!(inputProps.meta?.touched && inputProps.meta?.error)}
          type="search"
          InputProps={{
            ...params.InputProps,
            disableUnderline: inputProps.disableUnderline,
          }}
        />
      )}
      popupIcon={null}
      clearIcon={null}
      disablePortal
      PopperComponent={HighZPopper}
      sx={{ zIndex: 5000, ...sx }}
    />
  );
};

const FormLocationField = ({
  input,
  meta,
  afterSelect,
  alwaysDisplayErrors = false,
  locations,
  sx = {},
  ...props
}: {
  input: any;
  meta: MetaType;
  afterSelect?: (suggestion: string, placeId: string) => void;
  alwaysDisplayErrors?: boolean;
  locations?: { countryCode?: string }[];
  onSelectPlace?: onSelectPlaceType;
  sx?: React.CSSProperties;
  [key: string]: any;
}) => {
  const componentRestrictions = locations?.length
    ? {
        country: locations.map((x) => x.countryCode?.toLowerCase()).filter(Boolean),
      }
    : undefined;

  return (
    <>
      <PlacesAutocompleteField
        componentRestrictions={componentRestrictions}
        inputProps={{
          ...input,
          ...props,
          meta,
        }}
        afterSelect={afterSelect}
        sx={sx}
      />
      {(meta.dirty || alwaysDisplayErrors) && meta.error && (
        <div>
          <FormLabel error style={{ fontSize: 12, marginTop: 10 }}>
            {meta.error}
          </FormLabel>
        </div>
      )}
    </>
  );
};

export default FormLocationField;
